import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { addLead } from '../lib/api';
import { MANUAL_SOURCES, friendlyError, sourceLabel } from '../lib/format';
import type { Source } from '../lib/types';
import { Button, ErrorText, Modal, SelectField, TextArea, TextField } from '../components/ui';

export function AddLeadModal({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (leadId: string | null, message: string) => void }) {
  const { projects, members, isManager, me } = useAuth();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [source, setSource] = useState<Source>(isManager ? 'manual' : 'rep_sourced');
  const [note, setNote] = useState('');
  const [ownerId, setOwnerId] = useState(isManager ? '' : me?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setPhone(''); setName(''); setProjectId(''); setNote(''); setError(''); };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await addLead({ phone, name, projectId: projectId || null, source, note, ownerId: isManager ? ownerId || null : null });
      const msg = r.is_new
        ? 'Lead added.'
        : r.visible
          ? 'This buyer already had an open lead for that project. The enquiry was added to it.'
          : `This buyer is already with ${r.owner_name ?? 'a colleague'}. Your enquiry was added to their lead.`;
      reset();
      onAdded(r.visible ? r.lead_id : null, msg);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  const assignable = members.filter((m) => m.active && !m.on_leave);

  return (
    <Modal open={open} onClose={onClose} title="Add a lead">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <TextField label="Phone" required inputMode="tel" autoComplete="off" value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="98765 43210" hint="10 digits for India, or + and country code." />
        <TextField label="Buyer's name" value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Not decided yet</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </SelectField>
        <SelectField label="Where did this lead come from?" value={source} onChange={(e) => setSource(e.target.value as Source)}>
          {MANUAL_SOURCES.map((s) => <option key={s} value={s}>{sourceLabel[s]}</option>)}
        </SelectField>
        {isManager && (
          <SelectField label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
            hint="Leave unassigned for now. Automatic assignment arrives in week 4.">
            <option value="">Unassigned</option>
            {assignable.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </SelectField>
        )}
        <TextArea label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Wants 3 BHK, budget about ₹1.5 Cr" />
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Adding…' : 'Add lead'}</Button>
        </div>
      </form>
    </Modal>
  );
}
